"use client";

import { useSyncExternalStore, type ReactNode } from "react";
import { Auth0Provider, type AppState } from "@auth0/auth0-react";
import { useRouter } from "next/navigation";
import { useInitializeSession } from "@/lib/auth/useInitializeSession";
import { useInitializeAnalytics } from "@/lib/analytics";
import { takeStoredReturnTo, sanitizeReturnTo } from "@/lib/auth/return-to";
import { ClarityTestAuth0Provider } from "@/lib/auth/test-auth0-provider";
import { ReactQueryProvider } from "@/lib/query/ReactQueryProvider";
import { Toaster } from "@/components/ui/Toaster";
import { DatadogSessionContext } from "@/components/observability/DatadogSessionContext";
import { SessionReplayLifecycle } from "@/components/observability/SessionReplayLifecycle";
import { Spinner } from "../ui/Spinner";

function SessionInitializer({ children }: { children: ReactNode }) {
  useInitializeSession();
  useInitializeAnalytics();
  return <>{children}</>;
}

function AuthConfigurationError() {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="border-border bg-surface shadow-card w-full max-w-xl rounded-4xl border p-8">
        <p className="text-label-md text-content-tertiary tracking-[0.16em] uppercase">
          Auth Configuration
        </p>
        <h1 className="font-display text-foreground mt-4 text-4xl font-bold tracking-[-0.02em]">
          ClarityOS needs Auth0 environment variables.
        </h1>
        <p className="text-body-lg text-content-secondary mt-4">
          Set <code>NEXT_PUBLIC_AUTH0_DOMAIN</code> and{" "}
          <code>NEXT_PUBLIC_AUTH0_CLIENT_ID</code> for the ClarityOS app before
          using the temporary authenticated homepage.
        </p>
      </div>
    </div>
  );
}

function AuthRuntimeProviders({ children }: { children: ReactNode }) {
  const router = useRouter();
  const hasMounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  if (!hasMounted) {
    return <Spinner alignment="fixedCentered" />;
  }

  const appContent = (
    <ReactQueryProvider>
      <SessionInitializer>
        <DatadogSessionContext />
        <SessionReplayLifecycle />
        {children}
      </SessionInitializer>
      <Toaster />
    </ReactQueryProvider>
  );

  const isTestMode = window.__E2E_TEST_MODE__ === true;

  if (isTestMode) {
    return <ClarityTestAuth0Provider>{appContent}</ClarityTestAuth0Provider>;
  }

  const domain = process.env.NEXT_PUBLIC_AUTH0_DOMAIN ?? "";
  const clientId = process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID ?? "";
  const audience = process.env.NEXT_PUBLIC_AUTH0_AUDIENCE;
  const scope =
    process.env.NEXT_PUBLIC_AUTH0_SCOPE ??
    "openid profile email offline_access";

  if (!domain || !clientId) {
    return <AuthConfigurationError />;
  }

  function onRedirectCallback(appState?: AppState) {
    const storedReturnTo = takeStoredReturnTo();
    const returnTo = sanitizeReturnTo(appState?.returnTo ?? storedReturnTo);
    router.push(returnTo);
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope,
        audience,
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
      onRedirectCallback={onRedirectCallback}
    >
      {appContent}
    </Auth0Provider>
  );
}

export function AuthShellProviders({ children }: { children: ReactNode }) {
  return <AuthRuntimeProviders>{children}</AuthRuntimeProviders>;
}
